import React from 'react'
import { getOnlyTimeFromUtc } from '../../lib/datetime'
import { getMessage } from '../../lib/translator'
import { resolvePickingStatus } from './mapEntries'

const normalizeZoneKey = (zone) =>
  String(zone || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')

const formatQuantity = (value) => (value === null ? '—' : String(value))

const formatFlag = (value) =>
  getMessage(value ? 'picking.logs.flag.yes' : 'picking.logs.flag.no')

// A quantity/status that moved renders as "old → new"; one that the event
// carried without changing renders as a single value.
const DiffValue = ({ changed, oldValue, newValue, format }) =>
  changed ? (
    <React.Fragment>
      <span className="pl-diff-old">{format(oldValue)}</span>
      <span className="pl-diff-arrow" aria-hidden="true">
        →
      </span>
      <span className="pl-diff-new">{format(newValue)}</span>
    </React.Fragment>
  ) : (
    <span className="pl-diff-same">{format(newValue)}</span>
  )

const formatCount = (value) => String(value)

const formatPlain = (value) =>
  value === null || value === undefined || value === ''
    ? getMessage('picking.logs.value.none')
    : String(value)

const CodeList = ({ label, codes }) => {
  if (!codes || codes.length === 0) {
    return null
  }

  return (
    <div className="pl-packages-codes">
      <span className="pl-packages-codes-label">{label}</span>
      {codes.map((code) => (
        <span className="pl-code-chip" key={code}>
          {code}
        </span>
      ))}
    </div>
  )
}

const PackagesChange = ({ change }) => {
  if (!change) {
    return null
  }

  return (
    <div className="pl-packages" data-testid="picking-log-packages">
      <div className="pl-packages-count">
        <span className="pl-packages-label">
          {getMessage('picking.logs.crates')}
        </span>
        <DiffValue
          changed={change.oldCount !== change.newCount}
          oldValue={change.oldCount}
          newValue={change.newCount}
          format={formatCount}
        />
      </div>
      <CodeList
        label={getMessage('picking.logs.crates.added')}
        codes={change.added}
      />
      <CodeList
        label={getMessage('picking.logs.crates.removed')}
        codes={change.removed}
      />
    </div>
  )
}

const OtherChanges = ({ changes }) => {
  if (!changes || changes.length === 0) {
    return null
  }

  return (
    <details className="pl-other" data-testid="picking-log-other-changes">
      <summary className="pl-other-summary">
        {getMessage('picking.logs.otherChanges', { count: changes.length })}
      </summary>
      {changes.map((change) => (
        <div className="pl-other-row" key={change.key}>
          <span className="pl-other-label">{change.label}</span>
          <DiffValue
            changed
            oldValue={change.oldValue}
            newValue={change.newValue}
            format={formatPlain}
          />
        </div>
      ))}
    </details>
  )
}

const ItemChanges = ({ changes }) => {
  if (!changes || changes.length === 0) {
    return null
  }

  return (
    <div className="pl-items" data-testid="picking-log-item-changes">
      <div className="pl-items-title">
        {getMessage('picking.logs.itemChanges')}
      </div>
      {changes.map((change) => (
        <div
          className="pl-item"
          key={change.key}
          data-testid="picking-log-item"
        >
          <div className="pl-item-main">
            <span className="pl-item-name">{change.itemName || '—'}</span>
            {change.itemId ? (
              <span className="pl-item-id">#{change.itemId}</span>
            ) : null}
          </div>
          <div className="pl-item-diffs">
            <span className="pl-item-diff">
              <span className="pl-item-diff-label">
                {getMessage('picking.logs.itemChanges.quantity')}
              </span>
              <DiffValue
                changed={change.quantityChanged}
                oldValue={change.oldQuantity}
                newValue={change.newQuantity}
                format={formatQuantity}
              />
            </span>
            {change.statusChanged ? (
              <span className="pl-item-diff">
                <span className="pl-item-diff-label">
                  {getMessage('picking.logs.itemChanges.status')}
                </span>
                <DiffValue
                  changed
                  oldValue={change.oldStatus}
                  newValue={change.newStatus}
                  format={(value) => (value ? String(value) : '—')}
                />
              </span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  )
}

const LogEntry = ({ entry }) => {
  const { modifier: statusModifier, label: statusLabel } = resolvePickingStatus(
    entry.status
  )
  const timeLabel = entry.timestamp
    ? getOnlyTimeFromUtc(entry.timestamp)
    : '—'
  const zoneKey = normalizeZoneKey(entry.zone)
  // The name is resolved best-effort server-side; fall back to the id we do have.
  const staffLabel =
    entry.staffName || (entry.staffId ? `#${entry.staffId}` : '—')

  return (
    <div className="pl-event" data-testid="picking-log-entry">
      <div className="pl-event-time">{timeLabel}</div>

      <div className="pl-event-rail" aria-hidden="true">
        <span className="pl-event-line" />
        <span className={`pl-event-node pl-event-node--${statusModifier}`}>
          <span className="pl-event-dot" />
        </span>
      </div>

      <div className="pl-card">
        <div className="pl-card-head">
          <span
            className={`pl-status pl-status--${statusModifier}`}
            data-testid="picking-log-status"
          >
            <span className="pl-status-dot" aria-hidden="true" />
            {statusLabel}
          </span>
          <span className="pl-staff" data-testid="picking-log-staff">
            <span className="pl-staff-by">{getMessage('order.log.user')}</span>{' '}
            <span className="pl-staff-name">{staffLabel}</span>
          </span>
        </div>

        {(entry.packlistReference || entry.packlistTag || entry.zone) && (
          <div className="pl-chips">
            {entry.packlistReference ? (
              <span className="pl-chip" data-testid="picking-log-packlist">
                <span className="pl-chip-label">
                  {getMessage('picking.logs.packlist')}
                </span>
                <span className="pl-chip-value">{entry.packlistReference}</span>
              </span>
            ) : null}
            {entry.packlistTag ? (
              <span className="pl-chip" data-testid="picking-log-packlist-tag">
                <span className="pl-chip-label">
                  {getMessage('picking.logs.packlistTag')}
                </span>
                <span className="pl-chip-value">{entry.packlistTag}</span>
              </span>
            ) : null}
            {entry.zone ? (
              <span
                className={`pl-chip pl-chip--zone pl-chip--zone-${zoneKey}`}
                data-testid="picking-log-zone"
              >
                <span className="pl-chip-swatch" aria-hidden="true" />
                <span className="pl-chip-label">
                  {getMessage('picking.logs.zone')}
                </span>
                <span className="pl-chip-value">{entry.zone}</span>
              </span>
            ) : null}
          </div>
        )}

        {entry.checkRequired ? (
          <div className="pl-flag" data-testid="picking-log-check-required">
            <span className="pl-flag-label">
              {getMessage('picking.logs.checkRequired')}
            </span>
            <DiffValue
              changed
              oldValue={entry.checkRequired.oldValue}
              newValue={entry.checkRequired.newValue}
              format={formatFlag}
            />
          </div>
        ) : null}

        <PackagesChange change={entry.packagesChange} />

        <ItemChanges changes={entry.itemChanges} />

        <OtherChanges changes={entry.otherChanges} />
      </div>
    </div>
  )
}

export default LogEntry
